import { formatDateIn, toLocale } from '@zuruflex/i18n';
import type { DocumentRequirement, DocumentRequirementState, PartnerDocument } from '@zuruflex/types';
import { Badge, Button, cn } from '@zuruflex/ui';
import { AlertTriangle, Check, Clock, ExternalLink, FileUp, X } from 'lucide-react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';

/** How each state reads at a glance, before anybody has read the words. */
const TONE = {
  MISSING: 'outline',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'danger',
} as const;

/**
 * The same five states as a mark rather than a word.
 *
 * Tailwind reads these as source text, so each tint is written out in full
 * rather than composed from the state name at runtime — a `bg-${tone}/10`
 * would compile to nothing.
 */
const MARK: Record<
  DocumentRequirementState,
  { icon: ComponentType<{ className?: string }>; className: string }
> = {
  MISSING: { icon: FileUp, className: 'bg-surface-sunken text-text-subtle' },
  PENDING: { icon: Clock, className: 'bg-warning/12 text-warning' },
  APPROVED: { icon: Check, className: 'bg-success/12 text-success' },
  REJECTED: { icon: X, className: 'bg-danger/12 text-danger' },
  EXPIRED: { icon: AlertTriangle, className: 'bg-danger/12 text-danger' },
};

interface DocumentRowProps {
  requirement: DocumentRequirement<PartnerDocument>;
  onUpload: () => void;
  /** Worth having on file, but it does not hold the review up. */
  optional?: boolean;
}

/**
 * One line of paperwork: what it is, where it has got to, and the one thing
 * the partner can do about it.
 *
 * The second line never repeats the chip. A row that says "Not sent" twice
 * has spent its only line of explanation saying nothing — so the line
 * carries what the state actually implies for the partner instead: what to
 * photograph while it is missing, when it landed while it is with us, and
 * when it lapses once it is in.
 *
 * A rejection shows the reviewer's note in full. Sending somebody back to
 * upload the same file again, with only a red chip to go on, is how a queue
 * fills up with the same document three times.
 */
export function DocumentRow({ requirement, onUpload, optional = false }: DocumentRowProps) {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const { kind, state, document } = requirement;
  const mark = MARK[state];
  const Icon = mark.icon;

  /** What the second line says, which is never what the chip already says. */
  const detail = (): string => {
    if (state === 'PENDING' && document) {
      return t('verify.sentOn', { date: formatDateIn(document.createdAt, locale) });
    }
    if (state === 'APPROVED' && document?.expiresAt) {
      return t('verify.expiresOn', { date: formatDateIn(document.expiresAt, locale) });
    }
    if (state === 'APPROVED' && document) {
      return t('verify.acceptedOn', {
        date: formatDateIn(document.reviewedAt ?? document.createdAt, locale),
      });
    }
    if (state === 'EXPIRED' && document?.expiresAt) {
      return t('verify.expiredOn', { date: formatDateIn(document.expiresAt, locale) });
    }
    // MISSING and REJECTED both end in the same place — the upload dialog —
    // so both get told what a usable photo of this document looks like.
    return t(`verify.kindHint.${kind}`);
  };

  return (
    <li className="rounded-field border border-border-default bg-surface-raised px-3.5 py-3 transition-colors hover:border-border-strong">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2.5">
        {/* A basis wide enough that the chip and the buttons drop to their
            own line on a phone rather than squeezing "Business
            registration" into a two-line column three words wide. */}
        <div className="flex min-w-0 flex-1 basis-64 items-start gap-3">
          <span
            className={cn(
              'mt-0.5 grid size-8 shrink-0 place-items-center rounded-pill',
              mark.className,
            )}
            aria-hidden="true"
          >
            <Icon className="size-4" />
          </span>

          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
              {t(`common:documentKind.${kind}`)}
              {optional ? <Badge variant="outline">{t('verify.optional')}</Badge> : null}
            </p>
            <p className="mt-0.5 text-xs text-text-muted">{detail()}</p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Badge variant={TONE[state]}>{t(`common:documentState.${state}`)}</Badge>

          {/* The link is signed and short-lived, so it opens rather than
              being something to copy and keep. */}
          {document ? (
            <Button asChild size="icon-sm" variant="ghost">
              <a href={document.url} target="_blank" rel="noreferrer">
                <ExternalLink className="size-3.5" />
                <span className="sr-only">{t('common:action.open')}</span>
              </a>
            </Button>
          ) : null}

          {/* Outline, not yellow. The one yellow button on this page is the
              one that finishes it; a column of yellow upload buttons pulls
              the eye away from it and reads as five equal priorities. */}
          <Button size="sm" variant={state === 'APPROVED' ? 'ghost' : 'outline'} onClick={onUpload}>
            {state === 'MISSING' ? t('verify.upload') : t('verify.replace')}
          </Button>
        </div>
      </div>

      {state === 'REJECTED' && document?.reviewNote ? (
        <p className="mt-2.5 rounded-field bg-danger/10 px-3 py-2 text-xs text-danger">
          {document.reviewNote}
        </p>
      ) : null}

      {state === 'EXPIRED' ? (
        <p className="mt-2.5 rounded-field bg-danger/10 px-3 py-2 text-xs text-danger">
          {t('verify.expiredBody')}
        </p>
      ) : null}
    </li>
  );
}
